import { Alert, Button, Select, Space, Table, Typography, message } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { platformListEngineProfiles, platformUpdateEngineProfilePolicy } from '../api'
import type { EngineProfilePolicyRow, PromptLanguagePolicyValue, TranslationFailurePolicyValue } from '../types'
import { SectionCard, formatDateTime } from './adminShared'

const LANGUAGE_POLICY_OPTIONS: PromptLanguagePolicyValue[] = [
  'KEEP_ORIGINAL',
  'TRANSLATE_TO_EN',
  'AUTO_BY_MODEL',
  'TRANSLATE_TO_ZH',
]

const FAILURE_POLICY_OPTIONS: TranslationFailurePolicyValue[] = ['FALLBACK_ORIGINAL', 'BLOCK_SUBMISSION']

export default function EngineProfilesPage() {
  const { t } = useTranslation('platformAdmin')
  const [engines, setEngines] = useState<EngineProfilePolicyRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [drafts, setDrafts] = useState<Record<string, Partial<EngineProfilePolicyRow>>>({})
  const [saving, setSaving] = useState<Record<string, boolean>>({})

  const languagePolicyLabels = useMemo(
    () => ({
      KEEP_ORIGINAL: t('engineProfiles.promptLanguagePolicy.keepOriginal'),
      TRANSLATE_TO_EN: t('engineProfiles.promptLanguagePolicy.translateToEnglish'),
      TRANSLATE_TO_ZH: t('engineProfiles.promptLanguagePolicy.translateToChinese'),
      AUTO_BY_MODEL: t('engineProfiles.promptLanguagePolicy.autoByModel'),
    }),
    [t],
  )

  const failurePolicyLabels = useMemo(
    () => ({
      FALLBACK_ORIGINAL: t('engineProfiles.translationFailurePolicy.fallbackOriginal'),
      BLOCK_SUBMISSION: t('engineProfiles.translationFailurePolicy.blockSubmission'),
    }),
    [t],
  )

  const load = () => {
    setLoading(true)
    setError(null)
    platformListEngineProfiles()
      .then((res) => setEngines(res.data.engines))
      .catch((err) => setError((err as Error).message))
      .finally(() => setLoading(false))
  }

  useEffect(load, [])

  const saveEngine = async (row: EngineProfilePolicyRow) => {
    const draft = drafts[row.engine_id]
    if (!draft) return
    setSaving((s) => ({ ...s, [row.engine_id]: true }))
    try {
      const res = await platformUpdateEngineProfilePolicy(row.engine_id, {
        prompt_language_policy: draft.prompt_language_policy ?? row.prompt_language_policy,
        translation_failure_policy: draft.translation_failure_policy ?? row.translation_failure_policy,
      })
      setEngines((prev) => prev.map((e) => (e.engine_id === row.engine_id ? { ...e, ...res.data } : e)))
      setDrafts((d) => {
        const next = { ...d }
        delete next[row.engine_id]
        return next
      })
      message.success(t('engineProfiles.saveSuccess'))
    } catch (err) {
      message.error((err as Error).message)
    } finally {
      setSaving((s) => ({ ...s, [row.engine_id]: false }))
    }
  }

  return (
    <div>
      <div style={{ marginBottom: 4 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          {t('engineProfiles.title')}
        </Typography.Title>
        <Typography.Text type="secondary">{t('engineProfiles.subtitle')}</Typography.Text>
      </div>

      {error && <Alert type="error" message={error} showIcon style={{ margin: '12px 0' }} />}

      <SectionCard title={t('engineProfiles.sectionTitle')}>
        <Table
          className="pf-data-table"
          rowKey="engine_id"
          loading={loading}
          dataSource={engines}
          pagination={false}
          columns={[
            { title: t('engineProfiles.columns.engine'), dataIndex: 'display_name' },
            { title: t('engineProfiles.columns.engineId'), dataIndex: 'engine_id' },
            { title: t('engineProfiles.columns.type'), dataIndex: 'engine_type' },
            {
              title: t('engineProfiles.columns.promptLanguagePolicy'),
              dataIndex: 'prompt_language_policy',
              render: (value: PromptLanguagePolicyValue, row) => (
                <Select
                  style={{ minWidth: 220 }}
                  value={drafts[row.engine_id]?.prompt_language_policy ?? value}
                  options={LANGUAGE_POLICY_OPTIONS.map((opt) => ({
                    value: opt,
                    label: languagePolicyLabels[opt],
                  }))}
                  onChange={(next) =>
                    setDrafts((d) => ({
                      ...d,
                      [row.engine_id]: {
                        ...d[row.engine_id],
                        prompt_language_policy: next,
                      },
                    }))
                  }
                />
              ),
            },
            {
              title: t('engineProfiles.columns.translationFailurePolicy'),
              dataIndex: 'translation_failure_policy',
              render: (value: TranslationFailurePolicyValue, row) => (
                <Select
                  style={{ minWidth: 200 }}
                  value={drafts[row.engine_id]?.translation_failure_policy ?? value}
                  options={FAILURE_POLICY_OPTIONS.map((opt) => ({
                    value: opt,
                    label: failurePolicyLabels[opt],
                  }))}
                  onChange={(next) =>
                    setDrafts((d) => ({
                      ...d,
                      [row.engine_id]: {
                        ...d[row.engine_id],
                        translation_failure_policy: next,
                      },
                    }))
                  }
                />
              ),
            },
            {
              title: t('engineProfiles.columns.updatedAt'),
              dataIndex: 'updated_at',
              render: (v: string | null | undefined) => (v ? formatDateTime(v) : '—'),
            },
            {
              title: t('engineProfiles.columns.actions'),
              render: (_: unknown, row) => (
                <Space>
                  <Button
                    type="primary"
                    size="small"
                    disabled={!drafts[row.engine_id]}
                    loading={Boolean(saving[row.engine_id])}
                    onClick={() => saveEngine(row)}
                  >
                    {t('engineProfiles.save')}
                  </Button>
                </Space>
              ),
            },
          ]}
        />
      </SectionCard>
    </div>
  )
}
